/** The booking record's header (plan §1.1): the guest's name (→ the user), the state
 *  chip and the restech chip beside it; on the right the view switch (Spine · Tabs —
 *  remembered per operator, `?view=` wins), the Actions menu and Edit (`E`) — or, in edit
 *  mode, Close editor (`Esc`). The subtitle reads tenant · tour · the start
 *  in the tour's zone, labelled · ID · the booking system's id, each with its value
 *  actions.
 *
 *  No `console_bookings_edit` (or the kill switch off) → no Edit button at all: a
 *  disabled one would only advertise power the operator lacks; the Actions menu says the
 *  switch is off (`EDITING_OFF`). After the view switch, the records' `RecordHeaderActions`. */
import { Box, Link, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordEditContext } from '@/api/bookingRecord';
import type { BookingDetail } from '@/api/bookings';
import { PageHeader } from '@/components/PageHeader';
import { DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { EDITING_OFF } from '@/features/records/header/editingOff';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { formatZoned } from '@/utils/zonedTime';

import { BookingStateChip } from '../BookingStateChip';
import {
  altCustomIdActions,
  bookingIdActions,
  customIdActions,
  departureActions,
  guestActions,
  stateActions,
  tenantActions,
  tourActions,
  type BookingCtx,
} from '../overview/bookingLinks';
import { userName } from '../overview/common';

import { RestechChip } from './RestechChip';
import { MONO_SX } from './panels/displays';

export type BookingRecordView = 'spine' | 'tabs';

export interface RecordHeaderProps {
  id: number;
  detail: BookingDetail | undefined;
  editContext: BookingRecordEditContext | undefined;
  /** The active page tab's label (document title). */
  section: string;
  canEdit: boolean;
  editing: boolean;
  /** The kill switch is off: the Actions menu says so. */
  editingDisabled: boolean;
  onEdit: () => void;
  onClose: () => void;
  view: BookingRecordView;
  onView: (view: BookingRecordView) => void;
  integrationHref: string;
  actionGroups?: RecordActionGroup[];
}

export function RecordHeader({
  id,
  detail: b,
  editContext: ctx,
  section,
  canEdit,
  editing,
  editingDisabled,
  onEdit,
  onClose,
  view,
  onView,
  integrationHref,
  actionGroups = [],
}: RecordHeaderProps) {
  const links: BookingCtx = { tenantId: b?.tenant?.id ?? ctx?.tenant.id };
  const tz = ctx?.schedule.timezone ?? b?.tour?.start_timezone ?? null;
  const titleText = b?.guest ? userName(b.guest) || `Booking #${id}` : `Booking #${id}`;
  const title = b?.guest ? (
    <ValueActions actions={guestActions(b.guest, links)}>
      <Link component={RouterLink} to={`/users/${b.guest.id}`} underline="hover" color="inherit">
        {titleText}
      </Link>
    </ValueActions>
  ) : (
    titleText
  );

  const start = ctx?.schedule.start_ts ?? b?.start_ts ?? null;
  const when = start && tz ? formatZoned(start, tz, { year: 'auto' }) : (b?.date ?? null);
  const subtitle = b ? (
    <DetailSubtitle
      nowrap
      parts={[
        b.tenant && <ValueActions actions={tenantActions(b.tenant)}>{b.tenant.name}</ValueActions>,
        b.tour && (
          <ValueActions actions={tourActions(b.tour, links)}>
            <Link component={RouterLink} to={`/tours/${b.tour.id}`} underline="hover" color="inherit">
              {b.tour.name ?? `Tour #${b.tour.id}`}
            </Link>
          </ValueActions>
        ),
        when && <ValueActions actions={departureActions(b, links)}>{when}</ValueActions>,
        <ValueActions key="id" actions={bookingIdActions(id, links)}>
          ID {id}
        </ValueActions>,
        b.custom_id && (
          <ValueActions actions={customIdActions(b.custom_id, links)}>
            {b.booking_system_display || 'Booking'} ID{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {b.custom_id}
            </Box>
          </ValueActions>
        ),
        b.alt_custom_id && (
          <ValueActions actions={altCustomIdActions(b.alt_custom_id, links)}>
            Alt ID{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {b.alt_custom_id}
            </Box>
          </ValueActions>
        ),
      ]}
    />
  ) : (
    <Skeleton variant="text" width={420} />
  );

  const chips = b ? (
    <>
      <ValueActions chip actions={stateActions(b.booking_state_display, links)}>
        <BookingStateChip booking_state={b.booking_state} booking_state_display={b.booking_state_display} />
      </ValueActions>
      {ctx && tz && <RestechChip restech={ctx.restech} timezone={tz} integrationHref={integrationHref} />}
    </>
  ) : undefined;

  const actions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none', '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={(_, v: BookingRecordView | null) => v && onView(v)}
        aria-label="View"
      >
        <Tooltip describeChild title="The booking along its lifecycle, every stage on one page">
          <ToggleButton value="spine" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
            Spine
          </ToggleButton>
        </Tooltip>
        <Tooltip describeChild title="The booking one facet at a time — Booking, Guest, Money, Check-in…">
          <ToggleButton value="tabs" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
            Tabs
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
      <RecordHeaderActions
        groups={actionGroups}
        note={editingDisabled ? EDITING_OFF : null}
        canEdit={canEdit}
        editing={editing}
        onEdit={onEdit}
        onClose={onClose}
      />
    </Box>
  );

  return (
    <PageHeader
      helpStory="booking-detail"
      section={section}
      title={title}
      titleText={titleText}
      titleAdornment={chips}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
